'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { saveLandingPageChanges } from './landingPageUtils';
import { uploadAndReplaceImage, isImageElement, handleImageClick } from './imageUtils';
import { useFeatureFlagOn } from '@/hooks/useFeatureFlagOn';
import { useAnalytics } from '@/hooks/useAnalytics';
import repos from '@/lib/firebase/repos';
import { sendMessageToParent, createCommandHandler, type LandingPageCommand } from '@/utils/iframeMessaging';

// Re-export types for backward compatibility
export type { LandingPageMessage, LandingPageCommand } from '@/utils/iframeMessaging';
interface EditableLandingPageProps {
  landingPageId: string;
  initialHtmlContent: string;
}
export function EditableLandingPage({
  landingPageId,
  initialHtmlContent
}: EditableLandingPageProps) {
  const [htmlContent, setHtmlContent] = useState(initialHtmlContent);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingElement, setEditingElement] = useState<HTMLElement | null>(null);
  const [originalText, setOriginalText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  // Image editing state
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageCache = useRef(new WeakMap<HTMLImageElement, boolean>());
  const elementCache = useRef(new WeakMap<HTMLElement, boolean>());
  const keyHandlerCache = useRef(new WeakMap<HTMLElement, (e: KeyboardEvent) => void>());
  const {
    track
  } = useAnalytics();
  const [isEditModeDisabled, setIsEditModeDisabled] = useState(false);

  // Utility function to clean up all editor-related classes
  const cleanupEditorClasses = useCallback((element: HTMLElement) => {
    element.classList.remove('editing-element', 'hoverable-element');
    element.removeAttribute('contenteditable');

    // Remove the class attribute entirely if it's empty after cleanup
    if (element.classList.length === 0) {
      element.removeAttribute('class');
    }
  }, []);
  const finishEditing = useCallback(() => {
    if (!editingElement) return;

    // Remove element-specific keyboard event listener
    const keyHandler = keyHandlerCache.current.get(editingElement);
    if (keyHandler) {
      editingElement.removeEventListener('keydown', keyHandler, {
        capture: true
      });
      keyHandlerCache.current.delete(editingElement);
    }
    cleanupEditorClasses(editingElement);
    editingElement.blur();
    setIsEditing(false);
    setEditingElement(null);
    setOriginalText('');
    setIsSaving(false);
    isSavingRef.current = false;

    // Notify parent that editing status has changed
    sendMessageToParent('EDIT_STATUS_CHANGED', {
      isEditing: false
    });
  }, [editingElement, cleanupEditorClasses]);
  const cancelEditing = useCallback(() => {
    if (!editingElement) return;
    editingElement.innerText = originalText;
    finishEditing();

    // Notify parent that edit was cancelled
    sendMessageToParent('CANCEL_EDIT', {
      elementTag: editingElement.tagName,
      originalText
    });
  }, [editingElement, originalText, finishEditing]);
  const saveChanges = useCallback(async () => {
    if (!editingElement || isSavingRef.current || !containerRef.current) return;

    // Compute XPath relative to the landing page content root (exclude editor wrappers)
    const contentRoot = containerRef.current?.firstElementChild as HTMLElement | null;
    const xPath = getXPath(editingElement, contentRoot || undefined);
    const newText = editingElement.innerText?.trim() || '';

    // If the element contains nested elements (SVG icons, inline formatting),
    // preserve them by replacing the whole element with its cleaned outerHTML.
    // Plain-text elements keep the text path so newlines still convert to <br>.
    const hasElementChildren = editingElement.children.length > 0;
    let newHtml: string | undefined;
    if (hasElementChildren) {
      const cleanClone = editingElement.cloneNode(true) as HTMLElement;
      cleanupEditorClasses(cleanClone);
      newHtml = cleanClone.outerHTML;
    }

    // Track manual content edit attempt
    track('Landing Page Manual Edit', {
      landingPageId,
      elementType: editingElement.tagName.toLowerCase(),
      originalTextLength: originalText.length,
      newTextLength: newText.length,
      timestamp: new Date().toISOString(),
      status: 'attempted'
    });
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await saveLandingPageChanges({
        newText,
        landingPageId,
        xPath,
        newHtml,
        replaceAsHtml: !!newHtml
      });

      // Track successful manual edit
      track('Landing Page Manual Edit', {
        landingPageId,
        elementType: editingElement.tagName.toLowerCase(),
        originalTextLength: originalText.length,
        newTextLength: newText.length,
        success: true,
        timestamp: new Date().toISOString(),
        status: 'completed'
      });

      // Notify parent that changes were saved
      sendMessageToParent('SAVE_CHANGES', {
        elementTag: editingElement.tagName,
        originalText,
        newText,
        xPath,
        landingPageId
      });
    } catch (error) {
      // Track failed manual edit
      track('Landing Page Manual Edit', {
        landingPageId,
        elementType: editingElement.tagName.toLowerCase(),
        originalTextLength: originalText.length,
        newTextLength: newText.length,
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false,
        timestamp: new Date().toISOString(),
        status: 'failed'
      });
      // Error is already handled by the utility function
    } finally {
      finishEditing();
    }
  }, [editingElement, originalText, landingPageId, finishEditing, track]);
  const isTextElement = useCallback((element_0: HTMLElement): boolean => {
    // Check cache first
    const cached = elementCache.current.get(element_0);
    if (cached !== undefined) return cached;
    const textTags = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'DIV', 'A', 'LI', 'TD', 'TH', 'BUTTON', 'BLOCKQUOTE', 'CITE', 'STRONG', 'EM', 'BR'];

    // Fast checks first
    if (!textTags.includes(element_0.tagName)) {
      elementCache.current.set(element_0, false);
      return false;
    }

    // Special handling for BR elements (they don't have text content but are editable)
    if (element_0.tagName === 'BR') {
      elementCache.current.set(element_0, true);
      return true;
    }
    if (!element_0.innerText?.trim()) {
      elementCache.current.set(element_0, false);
      return false;
    }

    // Check if element is inside excluded containers. (Commented for reference)
    // if (element.closest('#countdown-timer')) {
    //   elementCache.current.set(element, false);
    //   return false;
    // }

    // Optimized media check - check tagName first before querySelector
    const hasMediaElements = element_0.getElementsByTagName('img').length > 0 || element_0.getElementsByTagName('video').length > 0 || element_0.getElementsByTagName('iframe').length > 0 || element_0.getElementsByTagName('canvas').length > 0;
    if (hasMediaElements) {
      elementCache.current.set(element_0, false);
      return false;
    }

    // Check if element has only text nodes and inline elements as direct children
    const children = element_0.childNodes;
    let hasOnlyTextAndInlineElements = true;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.nodeType === Node.TEXT_NODE) continue;
      if (child.nodeType === Node.ELEMENT_NODE) {
        const childElement = child as HTMLElement;
        const inlineTags = ['STRONG', 'B', 'EM', 'I', 'SPAN', 'CODE', 'MARK', 'SMALL', 'SUB', 'SUP', 'U', 'BR', 'SVG'];
        // SVG elements live in the SVG namespace, so their tagName preserves
        // the original lowercase ('svg', 'rect', etc.) — normalize before compare.
        if (!inlineTags.includes(childElement.tagName.toUpperCase())) {
          hasOnlyTextAndInlineElements = false;
          break;
        }
      } else {
        hasOnlyTextAndInlineElements = false;
        break;
      }
    }
    const result = hasOnlyTextAndInlineElements;
    elementCache.current.set(element_0, result);
    return result;
  }, []);
  const findSelectableTarget = useCallback((target: HTMLElement, container: HTMLElement | null): HTMLElement | null => {
    if (isTextElement(target)) return target;
    let current = target.parentElement;
    while (current && current !== container) {
      if (isTextElement(current)) return current;
      current = current.parentElement;
    }
    return null;
  }, [isTextElement]);
  const startEditing = useCallback((element_1: HTMLElement) => {
    setIsEditing(true);
    setEditingElement(element_1);
    setOriginalText(element_1.innerText || '');

    // Style the editing element and remove animation
    cleanupEditorClasses(element_1);
    element_1.classList.add('editing-element');
    element_1.contentEditable = 'true';
    element_1.focus();

    // Add element-specific keyboard event listener to prevent presentation navigation
    const elementKeyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === ' ') {
        // Stop propagation to prevent presentation slide navigation
        e.stopPropagation();
      }
    };

    // Store the handler in WeakMap for cleanup
    keyHandlerCache.current.set(element_1, elementKeyDownHandler);
    element_1.addEventListener('keydown', elementKeyDownHandler, {
      capture: true
    });

    // Select only the trailing text node when the element has nested children
    // (icon buttons, inline formatting). Selecting all node contents would also
    // select the SVG/child elements and the first keystroke would wipe them.
    const range = document.createRange();
    const hasElementChildren_0 = element_1.children.length > 0;
    let textNodeToSelect: Node | null = null;
    if (hasElementChildren_0) {
      for (let i_0 = element_1.childNodes.length - 1; i_0 >= 0; i_0--) {
        const child_0 = element_1.childNodes[i_0];
        if (child_0.nodeType === Node.TEXT_NODE && child_0.textContent?.trim()) {
          textNodeToSelect = child_0;
          break;
        }
      }
    }
    if (textNodeToSelect) {
      range.setStart(textNodeToSelect, 0);
      range.setEnd(textNodeToSelect, textNodeToSelect.textContent?.length || 0);
    } else {
      range.selectNodeContents(element_1);
      if (hasElementChildren_0) range.collapse(false);
    }
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    const contentRoot_0 = containerRef.current?.firstElementChild as HTMLElement | null;
    const xpath = getXPath(element_1, contentRoot_0 || undefined);
    const cleanElement = element_1.cloneNode(true) as HTMLElement;
    cleanupEditorClasses(cleanElement);

    // Notify parent that editing started
    sendMessageToParent('EDIT_STATUS_CHANGED', {
      isEditing: true,
      elementTag: element_1.tagName,
      elementText: element_1.innerText,
      xpath,
      elementHtml: cleanElement.outerHTML
    });
  }, [cleanupEditorClasses]);

  // Wrapper functions for image utilities
  const isImageElementWrapper = useCallback((element_2: HTMLElement): element_2 is HTMLImageElement => {
    return isImageElement(element_2, imageCache);
  }, []);
  const uploadAndReplaceImageWrapper = useCallback(async (imageElement: HTMLImageElement, file: File) => {
    await uploadAndReplaceImage({
      imageElement,
      file,
      landingPageId,
      containerRef,
      track: undefined,
      setIsUploadingImage,
      getXPath
    });
  }, [landingPageId]);
  const handleImageClickWrapper = useCallback((imageElement_0: HTMLImageElement) => {
    handleImageClick(imageElement_0, isEditing, isUploadingImage, uploadAndReplaceImageWrapper);
  }, [isEditing, isUploadingImage, uploadAndReplaceImageWrapper]);

  // Separate useEffect for message listener - always active to receive commands
  useEffect(() => {
    // Handle commands from parent window using the utility
    const handleParentCommand = createCommandHandler((command: LandingPageCommand) => {
      const {
        action,
        data
      } = command.payload;
      const container_0 = containerRef.current;
      switch (action) {
        case 'CANCEL_EDIT':
          if (isEditing) {
            cancelEditing();
          }
          break;
        case 'SAVE_CHANGES':
          if (isEditing) {
            saveChanges();
          }
          break;
        case 'EDIT_ELEMENT':
          if (data?.selector && !isEditing && container_0) {
            const element_3 = container_0.querySelector(data.selector) as HTMLElement;
            if (element_3 && isTextElement(element_3)) {
              startEditing(element_3);
            }
          }
          break;
        case 'DISABLE_EDIT_MODE':
          setIsEditModeDisabled(true);
          // Cancel any ongoing edit when disabling edit mode
          if (isEditing) {
            cancelEditing();
          }
          break;
        case 'ENABLE_EDIT_MODE':
          setIsEditModeDisabled(false);
          break;
      }
    });
    window.addEventListener('message', handleParentCommand);
    return () => {
      window.removeEventListener('message', handleParentCommand);
    };
  }, [isEditing, cancelEditing, saveChanges, startEditing, isTextElement]);

  // Separate useEffect for editing interactions - only active when edit mode is enabled
  useEffect(() => {
    const container_1 = containerRef.current;
    if (!container_1 || isEditModeDisabled) return;
    const handleMouseOver = (e_0: MouseEvent) => {
      if (isSaving || isUploadingImage) return;
      const target_0 = e_0.target as HTMLElement;
      const textTarget = findSelectableTarget(target_0, container_1);
      if (textTarget) {
        if (isEditing && textTarget === editingElement) return;
        cleanupEditorClasses(textTarget);
        textTarget.classList.add('hoverable-element');
      } else if (isImageElementWrapper(target_0) && !isEditing) {
        target_0.classList.add('hoverable-element');
      }
    };
    const handleMouseOut = (e_1: MouseEvent) => {
      if (isSaving || isUploadingImage) return;
      const target_1 = e_1.target as HTMLElement;
      const textTarget_0 = findSelectableTarget(target_1, container_1);
      if (textTarget_0) {
        if (isEditing && textTarget_0 === editingElement) return;
        cleanupEditorClasses(textTarget_0);
      } else if (isImageElementWrapper(target_1)) {
        cleanupEditorClasses(target_1);
      }
    };
    const handleClick = (e_2: MouseEvent) => {
      if (isEditing || isSaving) return;
      const target_2 = e_2.target as HTMLElement;
      const textTarget_1 = findSelectableTarget(target_2, container_1);
      if (textTarget_1) {
        e_2.preventDefault();
        startEditing(textTarget_1);
      } else if (isImageElementWrapper(target_2)) {
        e_2.preventDefault();
        handleImageClickWrapper(target_2);
      }
    };
    const handleKeyDown = (e_3: KeyboardEvent) => {
      if (!isEditing || !editingElement) return;
      if (e_3.key === 'Escape') {
        cancelEditing();
      } else if (e_3.key === 'Enter' && !e_3.shiftKey) {
        e_3.preventDefault();
        saveChanges();
      }
    };
    const handlePointerDownFinish = (e_4: PointerEvent) => {
      if (!isEditing || !editingElement) return;
      const target_3 = e_4.target as Node | null;
      if (!target_3) return;

      // If click is inside the editing element, ignore
      if (editingElement === target_3 || editingElement.contains(target_3 as Node)) return;
      const currentText = editingElement.innerText?.trim() || '';
      const originalTextTrim = originalText.trim();
      if (currentText !== originalTextTrim) {
        saveChanges();
      } else {
        finishEditing();
      }
    };

    // Add event listeners
    container_1.addEventListener('mouseover', handleMouseOver);
    container_1.addEventListener('mouseout', handleMouseOut);
    // Use pointerdown to commit edits only when the click happens inside this iframe/document
    document.addEventListener('pointerdown', handlePointerDownFinish);
    container_1.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      container_1.removeEventListener('mouseover', handleMouseOver);
      container_1.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('pointerdown', handlePointerDownFinish as any);
      container_1.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, editingElement, cancelEditing, saveChanges, finishEditing, originalText, isTextElement, findSelectableTarget, isImageElementWrapper, isSaving, isUploadingImage, cleanupEditorClasses, startEditing, isEditModeDisabled, handleImageClickWrapper]);
  const prevHtmlContentRef = useRef(htmlContent);

  // Cleanup effect for component unmount
  useEffect(() => {
    const currentEditingElement = editingElement;
    const currentKeyHandlerCache = keyHandlerCache.current;
    return () => {
      // Clean up any remaining keyboard event listeners on unmount
      if (currentEditingElement) {
        const keyHandler_0 = currentKeyHandlerCache.get(currentEditingElement);
        if (keyHandler_0) {
          currentEditingElement.removeEventListener('keydown', keyHandler_0, {
            capture: true
          });
        }
      }
    };
  }, [editingElement]);
  useEffect(() => {
    const unsubscribe = repos.collection('landingPages').subscribe(landingPageId, data_0 => {
      // Handle the case when document doesn't exist or data is undefined
      if (!data_0 || !data_0.htmlContent) return;
      if (prevHtmlContentRef.current !== data_0.htmlContent) {
        setHtmlContent(data_0.htmlContent);
        prevHtmlContentRef.current = data_0.htmlContent;
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [landingPageId]);
  return <div ref={containerRef} className='__poppy-lp-internal relative w-full' id='__poppy-lp-edit-status'
  // style={isEditModeDisabled ? { cursor: 'progress' } : {}}
  >
      <LandingPage htmlContent={htmlContent} />
      {isSaving && <div className='fixed right-4 top-4 z-[999999] rounded-md bg-blue-500 px-4 py-2 text-white shadow-lg'>
          <div className='flex items-center space-x-2'>
            <div className='size-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
            <span>Saving changes...</span>
          </div>
        </div>}
      {isUploadingImage && <div className='fixed right-4 top-4 z-[999999] rounded-md bg-blue-500 px-4 py-2 text-white shadow-lg'>
          <div className='flex items-center space-x-2'>
            <div className='size-4 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
            <span>Uploading image...</span>
          </div>
        </div>}
    </div>;
}
const LandingPage = memo(t0 => {
  const $ = _c(2);
  const {
    htmlContent
  } = t0;
  let t1;
  if ($[0] !== htmlContent) {
    t1 = <div className="poppy-published-page w-full" dangerouslySetInnerHTML={{
      __html: htmlContent
    }} />;
    $[0] = htmlContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
});
LandingPage.displayName = 'LandingPage';
function getXPath(element: HTMLElement, stopAt?: HTMLElement): string {
  if (!element) return '';
  if (element.id) {
    return `//*[@id="${element.id}"]`;
  }
  const segments: string[] = [];
  let current: HTMLElement | null = element;
  while (current && current.parentElement) {
    // Stop before including the provided root wrapper (client-only)
    if (stopAt && current === stopAt) {
      break;
    }
    const parent = current.parentElement;
    const currentTag = current.tagName.toLowerCase();
    const sameTagSiblings = Array.from(parent.children).filter((child): child is HTMLElement => (child as HTMLElement).tagName.toLowerCase() === currentTag);
    const position = sameTagSiblings.indexOf(current) + 1; // XPath is 1-based among same-tag siblings
    segments.unshift(`${currentTag}[${position}]`);
    current = parent;
  }

  // Root the XPath at the document body to match the server-side JSDOM structure
  const prefix = '/html/body[1]';
  const suffix = segments.join('/');
  return suffix ? `${prefix}/${suffix}` : prefix;
}